import React from 'react';
import { Row, Column } from '@carbon/react';
import { useGetWorkerStatusQuery } from '@/redux/new_services/workerStatus';
import { Panel, PanelHeader, PanelLabel } from '../Panel/Panel';

import './worker-status.scss';

// Polling cadence while the panel is mounted. The endpoint is a couple of
// Redis reads, so 3s feels live without putting load on the API process.
const POLL_INTERVAL_MS = 3000;

const JOB_LABELS = {
  convert_media: 'Element-Konvertierung',
  convert_holiday_media: 'Feiertag-Medium',
  convert_news_media: 'News-Medium',
};

const labelForJob = (jobName) => JOB_LABELS[jobName] || jobName;

// Best-effort attempt to pull a human-friendly hint out of a job's
// ArgsJSON. gocraft/work stores the args as a string of JSON, so we parse
// defensively — we'd rather show nothing than crash the panel because a
// job's payload changed shape.
const summariseArgs = (argsJson) => {
  if (!argsJson) return null;
  try {
    const args = JSON.parse(argsJson);
    return args?.filename || args?.real_filename || null;
  } catch (_e) {
    return null;
  }
};

// gocraft/work timestamps are Unix seconds. Render the elapsed time as a
// short, monotonic string so it doesn't change shape on every tick.
const formatElapsed = (startedAt, now) => {
  if (!startedAt) return '–';
  const secs = Math.max(0, now - startedAt);
  if (secs < 60) return `${secs}s`;
  const mins = Math.floor(secs / 60);
  const rem = secs % 60;
  return `${mins}m ${rem}s`;
};

// Single visible row per running job. Kept inline because it's tiny and
// nothing else uses it.
const RunningJobRow = ({ job, now }) => {
  const summary = summariseArgs(job.args_json);
  return (
    <li className="worker-status-panel__row">
      <span className="worker-status-panel__job-name">{labelForJob(job.job_name)}</span>
      {summary && (
        <span className="worker-status-panel__file">{summary}</span>
      )}
      <span className="worker-status-panel__elapsed">
        läuft seit {formatElapsed(job.started_at, now)}
      </span>
      {job.checkin && (
        <span className="worker-status-panel__checkin">{job.checkin}</span>
      )}
    </li>
  );
};

// Renders ONLY when there is at least one currently-running or queued
// job. Caller guards the admin check; we don't render to non-admins
// anyway because the endpoint replies 403.
const WorkerStatusPanel = () => {
  const { data, isError } = useGetWorkerStatusQuery(undefined, {
    pollingInterval: POLL_INTERVAL_MS,
  });

  const running = data?.running || [];
  const queued = data?.queued || [];
  // Single "now" reference per render so all row elapsed-times use the
  // same baseline and don't desync between rows in the same tick.
  const now = Math.floor(Date.now() / 1000);

  if (isError) return null;
  if (running.length === 0 && queued.length === 0) return null;

  const totalQueued = queued.reduce((sum, q) => sum + (q.count || 0), 0);

  return (
    <Row>
      <Column lg={16}>
        {/* Scope-Wrapper, der die fest verdrahtete 30rem-Hoehe des
            Panel-Containers wieder loslaesst, damit das Panel nur so hoch
            wird, wie sein Inhalt es braucht. Sieh dazu worker-status.scss. */}
        <div className="worker-status-panel">
          <Panel>
            <PanelHeader header helpText="Laufende Hintergrund-Konvertierungen des Workers. Nur fuer Admins sichtbar und nur, solange tatsaechlich etwas verarbeitet wird.">
              Worker aktiv
            </PanelHeader>
            <PanelLabel>
              {running.length > 0
                ? `${running.length} Job${running.length === 1 ? '' : 's'} in Bearbeitung`
                : 'Keine aktiven Jobs'}
              {totalQueued > 0 && ` • ${totalQueued} wartend`}
            </PanelLabel>
            {running.length > 0 && (
              <ul className="worker-status-panel__list">
                {running.map((job) => (
                  <RunningJobRow key={job.job_id || job.worker_id} job={job} now={now} />
                ))}
              </ul>
            )}
            {queued.length > 0 && (
              <ul className="worker-status-panel__queued">
                {queued.map((q) => (
                  <li key={q.job_name}>
                    {labelForJob(q.job_name)}: {q.count}
                  </li>
                ))}
              </ul>
            )}
          </Panel>
        </div>
      </Column>
    </Row>
  );
};

export default WorkerStatusPanel;
